import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { getEvents, safeCapture } from "../analytics";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { BodyCopy } from "../components/BodyCopy";
import {
  BankIcon,
  Button,
  CardIcon,
  CashSpecialIcon,
  Layout,
  SupportIcon,
  Title,
  WalletIcon,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { FlagCircle } from "../components/FlagCircle";
import { QuoteInfo } from "../components/QuoteInfo";
import { PayoutInfo } from "../components/PayoutInfo";
import {
  formatBankAccountLabel,
  looksLikePayerCode,
} from "../utils/bankAccountLabel";
import { Popup } from "../components/Popup";
import { AmountOutOfRangeInfo } from "../components/AmountOutOfRangeInfo";
import {
  SelectPayoutMethodSlot,
  type FlatPayoutPick,
} from "../components/SelectPayoutMethodSlot";
import { SelectDestinationCountrySlot } from "../components/SelectDestinationCountrySlot";
import {
  PayoutDetailsSlot,
  type PayoutDetailsResult,
} from "../components/PayoutDetailsSlot";
import { useRequestAmountEnvelope } from "../hooks/useRequestAmountEnvelope";
import { useDestinationCards } from "../hooks/useDestinationCards";
import { useBankAccounts } from "../hooks/useBankAccounts";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { shouldPromptPhone } from "../utils/phoneGate";
import { ensureSmsConsent } from "../components/notifications/smsConsent";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useRequestStore } from "../store/useRequestStore";
import { useComposeStore } from "../store/useComposeStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { usePreQuote } from "../hooks/usePreQuote";
import {
  useCatalogCountries,
  useCatalogPayers,
  useCatalogPayoutMethods,
} from "../hooks/useCatalog";
import {
  corridorCurrencyCodesByDestination,
  currenciesForCountry,
  currencyForCountry,
  expandCountryCurrencyOptions,
  mergeCatalogWithCorridorCurrencies,
  pickDefaultDestinationCurrency,
} from "../api/catalog";
import type { IBankAccountPaymentMethod } from "../interfaces/payment-method";
import { PayoutMethodType } from "../interfaces/payout";
import {
  preparePayoutRail,
  sanitizePayoutAfterPrepare,
} from "../utils/prepare-payout-rail";
import { isPayoutCardValidationStale } from "../utils/pre-validate-payout-card";
import { buildRequestDraft } from "../utils/request-draft";
import { getBrandInfo } from "../constants/card-brands";
import { isDeadLinkError } from "../services/api";
import { catalogPayerDisplayName } from "../utils/payerCode";
import { offerableSavedBankAccounts } from "../utils/offerableBankAccounts";
import { payerCodesEquivalent } from "../utils/payerCode";
import type { PreQuoteInput } from "../api/pricing";
import type { FundRequestPayoutMethod } from "../api/fund-request";
import {
  findCorridor,
  resolveInitialCorridor,
  resolveOriginForDestination,
  seedDestinationCountry,
  supportedDestinationCountries,
} from "../utils/corridors";
import { countryDisplayName } from "../utils/country-display";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { Navbar } from "../components/Navbar";


/** Payout methods this flow can compose, mapped from catalog vocabulary. */
const SUPPORTED_PAYOUT_METHODS: PayoutMethodType[] = [
  PayoutMethodType.BANK_ACCOUNT,
  PayoutMethodType.CASH_PICKUP,
  PayoutMethodType.PUSH_TO_CARD,
  PayoutMethodType.MOBILE_WALLET,
];

const SUPPORTED_PAYOUT_SET = new Set<string>(SUPPORTED_PAYOUT_METHODS);

const filterPayoutMethods = (methods: string[]): PayoutMethodType[] =>
  methods.filter((m): m is PayoutMethodType => SUPPORTED_PAYOUT_SET.has(m));

type PayoutStep = "method" | "details";

/**
 * Compose-a-request screen. The requester enters how much they want to receive
 * (anchored on the destination/receive leg), can change destination country
 * and currency (when a country supports more than one),
 * then how they want to be paid out: the payout method, the payer, and the
 * per-method details. Available payout methods come from the partner corridor
 * config ∩ catalog AVAILABLE, then (once a pre-quote lands) only methods that
 * appear in `preQuote.options` — same quotability gate as flow-send, so we
 * never surface Cash pickup / bank / wallet chips with zero offerable payers.
 * A debounced pre-quote also supplies economics and the `capabilityKey` of the
 * chosen option. Confirming stores the draft and routes to CIP (when required)
 * and then the minting flow.
 */
export const TransferPage = memo(() => {
  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("route_amount"));
  }, []);

  const { t, i18n } = useTranslation();
  const { theme, amountState } = useStore();
  const { session } = useSessionStore();
  const { setDraft } = useRequestStore();
  const { redirectTo, linkId: linkIdFromRoute } = useRouteWithToken();
  const navigate = useNavigate();

  const { amount } = amountState;
  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, bodyCopy, dropzoneColors } =
    colorConfig;

  const [showDestinationPopup, setShowDestinationPopup] = useState(false);
  const [showPayoutPopup, setShowPayoutPopup] = useState(false);
  const [amountLimitPopup, setAmountLimitPopup] = useState<{
    kind: "below_min" | "above_max";
    bound: number;
    currency: string;
  } | null>(null);
  const [payoutStep, setPayoutStep] = useState<PayoutStep>("method");
  const [pendingMethod, setPendingMethod] = useState<PayoutMethodType | null>(
    null
  );
  const [shakePayout, setShakePayout] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [prepareError, setPrepareError] = useState<string | null>(null);

  const config = session.data?.config;
  const corridors = config?.corridors ?? [];
  const initialCorridor = resolveInitialCorridor(
    corridors,
    config?.defaultCorridor
  );

  const {
    destinationCountry: storedDestination,
    setDestinationCountry,
    destinationCurrency: storedCurrency,
    setDestinationCurrency,
    selection,
    setSelection,
  } = useComposeStore();

  const seededDestination = seedDestinationCountry(
    corridors,
    config?.defaultCorridor
  );
  const destinationCountry = storedDestination ?? seededDestination ?? "";
  const pinnedDestination =
    initialCorridor?.destinationCountryCode ?? seededDestination ?? "";

  useEffect(() => {
    if (storedDestination == null && seededDestination) {
      setDestinationCountry(seededDestination);
    }
  }, [storedDestination, seededDestination, setDestinationCountry]);

  const sourceCountry =
    resolveOriginForDestination(corridors, destinationCountry) ??
    initialCorridor?.originCountryCode ??
    "US";

  // Destination-card dry-run is corridor-scoped: clear it when the route changes.
  useEffect(() => {
    const current = useComposeStore.getState().selection;
    if (
      !current ||
      current.method !== PayoutMethodType.PUSH_TO_CARD ||
      current.payout.source !== "new"
    ) {
      return;
    }
    const payout = current.payout;
    if (!payout.cvsValidationId && !payout.validatedFor) return;
    if (
      !isPayoutCardValidationStale(
        payout.validatedFor,
        sourceCountry,
        destinationCountry
      )
    ) {
      return;
    }
    setSelection({
      ...current,
      payout: {
        ...payout,
        cvsValidationId: undefined,
        numberToken: undefined,
        validatedFor: undefined,
      },
    });
  }, [sourceCountry, destinationCountry, setSelection]);

  const corridorDestinations = useMemo(() => {
    const fromCorridors = supportedDestinationCountries(corridors);
    if (fromCorridors.length > 0) return fromCorridors;
    return seededDestination ? [seededDestination] : [];
  }, [corridors, seededDestination]);

  const { data: countries } = useCatalogCountries({
    originCountryCode: sourceCountry,
  });

  const corridorCodesByDest = useMemo(
    () => corridorCurrencyCodesByDestination(corridors),
    [corridors]
  );

  const destinationCountriesForPicker = useMemo(
    () =>
      expandCountryCurrencyOptions(
        mergeCatalogWithCorridorCurrencies(
          (countries ?? []).map((c) => ({
            ...c,
            countryName: countryDisplayName(c.countryCode, i18n.language),
          })),
          corridorDestinations,
          corridorCodesByDest,
          (code) => countryDisplayName(code, i18n.language)
        )
      ),
    [countries, corridorDestinations, corridorCodesByDest, i18n.language]
  );

  const sourceCurrency = useMemo(() => {
    if (
      sourceCountry === config?.defaultCorridor?.originCountryCode &&
      config?.sourceCurrency
    ) {
      return config.sourceCurrency;
    }
    const match = (countries ?? []).find((c) => c.countryCode === sourceCountry);
    return currencyForCountry(match) ?? config?.sourceCurrency ?? "USD";
  }, [
    sourceCountry,
    config?.defaultCorridor?.originCountryCode,
    config?.sourceCurrency,
    countries,
  ]);

  const catalogDestination = useMemo(() => {
    const rows = destinationCountriesForPicker.filter(
      (o) => o.countryCode === destinationCountry
    );
    if (rows.length === 0) {
      return (countries ?? []).find((c) => c.countryCode === destinationCountry);
    }
    return {
      countryCode: destinationCountry,
      countryName: rows[0].countryName,
      currencyCodes: rows.map((r) => r.currencyCode),
      supportedByCorridors: rows[0].supportedByCorridors,
      effectiveStatus: rows[0].effectiveStatus,
    };
  }, [destinationCountriesForPicker, countries, destinationCountry]);

  useEffect(() => {
    if (!destinationCountry) return;
    const codes = currenciesForCountry(catalogDestination);
    if (codes.length === 0) return;
    const pinned =
      destinationCountry === pinnedDestination
        ? config?.destinationCurrency
        : undefined;
    const preferred = pickDefaultDestinationCurrency(
      catalogDestination,
      pinned
    );
    if (!preferred) return;
    if (storedCurrency == null || !codes.includes(storedCurrency)) {
      setDestinationCurrency(preferred);
    }
  }, [
    destinationCountry,
    catalogDestination,
    storedCurrency,
    pinnedDestination,
    config?.destinationCurrency,
    setDestinationCurrency,
  ]);

  const destinationCurrency =
    storedCurrency ??
    pickDefaultDestinationCurrency(
      catalogDestination,
      destinationCountry === pinnedDestination
        ? config?.destinationCurrency
        : undefined
    ) ??
    "USD";

  const numericAmount = amount ? parseFloat(amount) : NaN;
  const hasAmount = !isNaN(numericAmount) && numericAmount > 0;

  const preQuoteInput = useMemo<PreQuoteInput | null>(() => {
    if (!hasAmount) return null;
    return {
      originCountryCode: sourceCountry,
      destinationCountryCode: destinationCountry,
      amount: numericAmount,
      anchor: "receive",
      destinationCurrency,
      sourceCurrency,
      vary: "payer",
      // Facets are pre-collapse so every quotable payout method surfaces even
      // when vary=payer keeps one option per payer in `options`.
      include: ["facets", "options", "summary"],
    };
  }, [
    hasAmount,
    numericAmount,
    sourceCountry,
    destinationCountry,
    destinationCurrency,
    sourceCurrency,
  ]);

  const { result: preQuote, loading: preQuoteLoading } =
    usePreQuote(preQuoteInput);

  const { cards: destinationCards, loading: destinationCardsLoading } =
    useDestinationCards();

  const { accounts: vaultAccounts, loading: vaultAccountsLoading } =
    useBankAccounts(destinationCountry);

  // Base list from partner claim (`config.corridors[].payoutMethods`).
  const corridor = useMemo(
    () => findCorridor(corridors, sourceCountry, destinationCountry),
    [corridors, sourceCountry, destinationCountry]
  );

  const basePayoutMethods = useMemo(
    () => filterPayoutMethods(corridor?.payoutMethods ?? []),
    [corridor]
  );

  // Catalog narrows to methods actually AVAILABLE on this corridor.
  const { data: payoutFromCatalog, loading: catalogPayoutLoading } =
    useCatalogPayoutMethods(
      sourceCountry && destinationCountry
        ? {
            originCountryCode: sourceCountry,
            destinationCountryCode: destinationCountry,
          }
        : null
    );

  const catalogPayoutMethods = useMemo<PayoutMethodType[]>(() => {
    if (!payoutFromCatalog) return basePayoutMethods;
    const allowed = new Set(
      payoutFromCatalog
        .filter((p) => p.effectiveStatus === "AVAILABLE")
        .map((p) => p.payoutMethod)
    );
    return basePayoutMethods.filter((m) => allowed.has(m));
  }, [basePayoutMethods, payoutFromCatalog]);

  // Quotability gate (parity with flow-send): once a pre-quote for this amount
  // exists, only show methods that have ≥1 offerable option. Prefer pre-collapse
  // facets so a payout method is not hidden when vary=payer collapsed it away.
  // Until then keep the catalog list so the grid does not flash empty.
  const availablePayoutMethods = useMemo<PayoutMethodType[]>(() => {
    if (!preQuote) return catalogPayoutMethods;
    const offerable = new Set<PayoutMethodType>();
    const facetMethods = preQuote.facets?.payoutMethods ?? [];
    if (facetMethods.length > 0) {
      for (const facet of facetMethods) {
        if (SUPPORTED_PAYOUT_SET.has(facet.payoutMethod)) {
          offerable.add(facet.payoutMethod as PayoutMethodType);
        }
      }
    } else {
      for (const option of preQuote.options ?? []) {
        if (SUPPORTED_PAYOUT_SET.has(option.payoutMethod)) {
          offerable.add(option.payoutMethod as PayoutMethodType);
        }
      }
    }
    return catalogPayoutMethods.filter((m) => offerable.has(m));
  }, [catalogPayoutMethods, preQuote]);

  const bankMethodOptions = useMemo(
    () =>
      (preQuote?.options ?? []).filter((o) => o.payoutMethod === "BANK_ACCOUNT"),
    [preQuote?.options]
  );
  const bankAvailable = availablePayoutMethods.includes(
    PayoutMethodType.BANK_ACCOUNT
  );
  const { data: bankPayers, loading: bankPayersLoading } = useCatalogPayers(
    bankAvailable && destinationCountry
      ? {
          originCountryCode: sourceCountry,
          destinationCountryCode: destinationCountry,
          payoutMethod: "BANK_ACCOUNT",
          currencyCode: destinationCurrency,
        }
      : null,
    bankAvailable
  );
  const catalogBankPayerCodes = useMemo(() => {
    if (!bankPayers) return null;
    return bankPayers
      .filter((p) => p.effectiveStatus === "AVAILABLE")
      .map((p) => p.payerCode);
  }, [bankPayers]);
  const offerableAccounts = useMemo(
    () =>
      offerableSavedBankAccounts(
        vaultAccounts,
        bankMethodOptions,
        catalogBankPayerCodes
      ),
    [vaultAccounts, bankMethodOptions, catalogBankPayerCodes]
  );
  const needsBankCatalog =
    bankAvailable &&
    bankMethodOptions.length > 0 &&
    !bankMethodOptions.some((o) => o.payerCode);
  const accountsLoading =
    bankAvailable &&
    (vaultAccountsLoading ||
      (needsBankCatalog && (bankPayersLoading || catalogBankPayerCodes == null)));
  const payoutPickerLoading =
    (availablePayoutMethods.includes(PayoutMethodType.PUSH_TO_CARD) &&
      destinationCardsLoading) ||
    accountsLoading ||
    (availablePayoutMethods.length === 0 &&
      (catalogPayoutLoading || preQuoteLoading));
  const bankLabels = useMemo(() => {
    const map: Record<string, string> = {};
    for (const payer of bankPayers ?? []) {
      if (payer.payerCode) {
        map[payer.payerCode.toUpperCase()] = payer.displayName;
      }
    }
    return map;
  }, [bankPayers]);

  // Drop a prior selection / in-progress picker if the method is no longer
  // quotable (e.g. amount change removed the only CASH_PICKUP SKUs).
  useEffect(() => {
    if (
      selection &&
      !availablePayoutMethods.includes(selection.method)
    ) {
      setSelection(null);
    }
    if (
      pendingMethod &&
      !availablePayoutMethods.includes(pendingMethod)
    ) {
      setPendingMethod(null);
      setPayoutStep("method");
    }
  }, [availablePayoutMethods, selection, setSelection, pendingMethod]);

  // Route is unavailable when a pre-quote for this amount returns no options.
  const routeUnavailable =
    !preQuoteLoading &&
    Boolean(preQuote) &&
    (preQuote?.options.length ?? 0) === 0;

  const handleDestinationChange = useCallback(
    (countryCode: string, currencyCode: string) => {
      if (
        countryCode !== destinationCountry ||
        currencyCode !== destinationCurrency
      ) {
        setSelection(null);
        setPayoutStep("method");
        setPendingMethod(null);
        setShowPayoutPopup(false);
      }
      setDestinationCountry(countryCode);
      setDestinationCurrency(currencyCode);
      safeCapture(getEvents().DESTINATION_SELECTED, {
        to_country: countryCode,
        to_currency: currencyCode,
      });
    },
    [
      destinationCountry,
      destinationCurrency,
      setDestinationCountry,
      setDestinationCurrency,
      setSelection,
    ]
  );

  const openMethodPicker = useCallback(() => {
    // Need an amount so the pre-quote can decide which methods are offerable;
    // opening earlier would show catalog-only chips that may be dead ends.
    if (!hasAmount || routeUnavailable) return;
    setPayoutStep("method");
    setPendingMethod(null);
    setShowPayoutPopup(true);
  }, [hasAmount, routeUnavailable]);

  const handleSelectMethod = useCallback((method: PayoutMethodType) => {
    setPendingMethod(method);
    setPayoutStep("details");
    safeCapture(getEvents().PAYOUT_SELECTED, { payout_method: method });
  }, []);

  const handleDetailsSubmit = useCallback(
    (result: PayoutDetailsResult) => {
      if (!pendingMethod) return;
      setSelection({
        method: pendingMethod,
        option: result.option,
        payout: result.payout,
      });
      setShowPayoutPopup(false);
      if (result.option?.capabilityKey) {
        safeCapture(getEvents().CAPABILITY_SELECTED, {
          capability_key: result.option.capabilityKey,
          payout_method: pendingMethod,
        });
      }
    },
    [pendingMethod, setSelection]
  );

  const pushToCardOption = useMemo(
    () =>
      (preQuote?.options ?? []).find(
        (o) => o.payoutMethod === "PUSH_TO_CARD"
      ) ?? null,
    [preQuote?.options]
  );

  // After AddCardProcessing returns with option:null, bind the live pre-quote SKU.
  useEffect(() => {
    if (
      !selection ||
      selection.method !== PayoutMethodType.PUSH_TO_CARD ||
      selection.option ||
      !pushToCardOption
    ) {
      return;
    }
    setSelection({ ...selection, option: pushToCardOption });
  }, [selection, pushToCardOption, setSelection]);

  const handleSelectCardInstrument = useCallback(
    (card: {
      paymentMethodId: string;
      brand: string;
      last4: string;
      cardExpiry?: string;
      cardholderName?: string;
    }) => {
      setSelection({
        method: PayoutMethodType.PUSH_TO_CARD,
        option: pushToCardOption,
        payout: {
          source: "saved",
          paymentMethodId: card.paymentMethodId,
          brand: (card.brand || "").toUpperCase(),
          last4: card.last4,
          ...(card.cardExpiry ? { cardExpiry: card.cardExpiry } : {}),
          ...(card.cardholderName
            ? { cardholderName: card.cardholderName }
            : {}),
        },
      });
      setShowPayoutPopup(false);
      safeCapture(getEvents().PAYOUT_SELECTED, {
        payout_method: PayoutMethodType.PUSH_TO_CARD,
      });
      if (pushToCardOption?.capabilityKey) {
        safeCapture(getEvents().CAPABILITY_SELECTED, {
          capability_key: pushToCardOption.capabilityKey,
          payout_method: PayoutMethodType.PUSH_TO_CARD,
        });
      }
    },
    [pushToCardOption, setSelection]
  );

  const handleSelectBankInstrument = useCallback(
    (account: IBankAccountPaymentMethod) => {
      const bankCode = (account.bankName || "").toUpperCase();
      const option =
        bankMethodOptions.find((o) =>
          payerCodesEquivalent(o.payerCode, bankCode)
        ) ??
        bankMethodOptions[0] ??
        null;
      setSelection({
        method: PayoutMethodType.BANK_ACCOUNT,
        option,
        payout: {
          source: "saved",
          paymentMethodId: account.paymentMethodId,
          bankId: bankCode,
          last4: account.maskedAccountNumber.replace(/\D/g, "").slice(-4),
        },
      });
      setShowPayoutPopup(false);
      safeCapture(getEvents().PAYOUT_SELECTED, {
        payout_method: PayoutMethodType.BANK_ACCOUNT,
      });
      if (option?.capabilityKey) {
        safeCapture(getEvents().CAPABILITY_SELECTED, {
          capability_key: option.capabilityKey,
          payer_code: option.payerCode,
          payout_method: PayoutMethodType.BANK_ACCOUNT,
        });
      }
    },
    [bankMethodOptions, setSelection]
  );

  const handlePayoutPick = useCallback(
    (pick: FlatPayoutPick) => {
      if (pick.kind === "card") {
        handleSelectCardInstrument(pick.card);
        return;
      }
      if (pick.kind === "bank") {
        handleSelectBankInstrument(pick.account);
        return;
      }
      if (pick.kind === "add") {
        safeCapture(getEvents().PAYOUT_SELECTED, {
          payout_method: PayoutMethodType.PUSH_TO_CARD,
        });
        redirectTo(APP_ROUTES.ADD_CARD_PAGE);
        return;
      }
      if (pick.kind === "addBank") {
        handleSelectMethod(PayoutMethodType.BANK_ACCOUNT);
        return;
      }
      handleSelectMethod(pick.method);
    },
    [
      handleSelectCardInstrument,
      handleSelectBankInstrument,
      handleSelectMethod,
      redirectTo,
    ]
  );

  const payoutIcon = useMemo(() => {
    switch (selection?.method) {
      case PayoutMethodType.BANK_ACCOUNT:
        return <BankIcon size={20} />;
      case PayoutMethodType.CASH_PICKUP:
        return <CashSpecialIcon size={20} />;
      case PayoutMethodType.PUSH_TO_CARD: {
        const brandInfo = selection.payout.brand
          ? getBrandInfo(selection.payout.brand)
          : undefined;
        if (brandInfo) {
          return (
            <img
              src={brandInfo.logo}
              alt={brandInfo.label}
              width={28}
              height={18}
              style={{ objectFit: "contain" }}
            />
          );
        }
        return <CardIcon size={20} />;
      }
      default:
        return <WalletIcon size={20} />;
    }
  }, [selection]);

  // Catalog owns the human-readable payer/bank/carrier name; the compose
  // selection only stores the canonical code (e.g. `GT_BANCO_GYT_CASH`).
  const pinnedPayerCode = useMemo(() => {
    if (!selection) return undefined;
    const { payout, method } = selection;
    if (method === PayoutMethodType.CASH_PICKUP) return payout.payerId;
    if (method === PayoutMethodType.BANK_ACCOUNT) return payout.bankId;
    if (method === PayoutMethodType.MOBILE_WALLET) return payout.carrierId;
    return undefined;
  }, [selection]);

  const { data: catalogPayers } = useCatalogPayers(
    destinationCountry && selection
      ? {
          originCountryCode: sourceCountry,
          destinationCountryCode: destinationCountry,
          payoutMethod: selection.method,
          includeUnavailable: true,
        }
      : null,
    Boolean(pinnedPayerCode)
  );

  const payerDisplayName = useMemo(() => {
    if (!pinnedPayerCode) return "";
    return catalogPayerDisplayName(pinnedPayerCode, catalogPayers) ?? "";
  }, [pinnedPayerCode, catalogPayers]);

  const payoutSummary = useMemo(() => {
    if (!selection) return t("transfer.selectPayoutMethod");
    const method = t(`payout.methods.${selection.method}`);
    const { payout } = selection;
    switch (selection.method) {
      case PayoutMethodType.BANK_ACCOUNT: {
        const tail =
          payout.accountNumber?.replace(/\D/g, "").slice(-4) || payout.last4;
        const stored =
          payout.bankId && !looksLikePayerCode(payout.bankId)
            ? payout.bankId
            : null;
        const line = formatBankAccountLabel(stored, tail);
        if (line) return line;
        if (tail) return `${method} · •••• ${tail}`;
        return payerDisplayName ? `${method} · ${payerDisplayName}` : method;
      }
      case PayoutMethodType.CASH_PICKUP:
        return payerDisplayName
          ? `${method} · ${payerDisplayName}`
          : method;
      case PayoutMethodType.PUSH_TO_CARD:
        if (payout.brand && payout.last4) {
          return `${payout.brand.toUpperCase()}-${payout.last4}`;
        }
        return payout.last4 ? `${method} · ${payout.last4}` : method;
      case PayoutMethodType.MOBILE_WALLET:
        if (payout.phone) return `${method} · ${payout.phone}`;
        return payerDisplayName ? `${method} · ${payerDisplayName}` : method;
      default:
        return method;
    }
  }, [selection, payerDisplayName, t]);

  const payoutReady = Boolean(selection);

  const envelopeInput = useMemo(() => {
    if (!hasAmount || !selection || routeUnavailable) return null;
    const payerCode =
      selection.payout.payerId ??
      selection.payout.bankId ??
      selection.option?.payerCode ??
      undefined;
    return {
      enabled: true,
      fallbackCurrency: destinationCurrency,
      originCountryCode: sourceCountry,
      destinationCountryCode: destinationCountry,
      sourceCurrency,
      destinationCurrency,
      payoutMethod: selection.method,
      amount: numericAmount,
      amountAnchor: "receive" as const,
      ...(payerCode ? { payerCode } : {}),
      ...(selection.payout.stateCode
        ? { stateCode: selection.payout.stateCode }
        : {}),
    };
  }, [
    hasAmount,
    selection,
    routeUnavailable,
    sourceCountry,
    destinationCountry,
    sourceCurrency,
    destinationCurrency,
    numericAmount,
  ]);

  const {
    loading: envelopeLoading,
    verdict: envelopeVerdict,
    displayBound: envelopeDisplayBound,
    recheck: recheckEnvelope,
  } = useRequestAmountEnvelope(envelopeInput);

  const amountOutOfRange =
    envelopeVerdict === "below_min" || envelopeVerdict === "above_max";
  const canConfirm = hasAmount && payoutReady && !routeUnavailable;
  // Send parity: rainbow only when ready and in-range; CTA stays pressable.
  const showQuoteLoading = envelopeLoading && canConfirm;
  const showRainbow =
    (canConfirm || confirmLoading) && !envelopeLoading && !amountOutOfRange;
  const payoutPending = hasAmount && !payoutReady;

  const prepareErrorMessage = useCallback(
    (code: string | undefined): string => {
      switch (code) {
        case "PROVISION_FAILED":
          return t("transfer.prepare.errorProvision");
        case "REGISTER_FAILED":
          return t("transfer.prepare.errorRegister");
        case "READINESS_FAILED":
          return t("transfer.prepare.errorReadiness");
        case "VGS_CONFIG_UNAVAILABLE":
          return t("vgs.configUnavailable");
        case "CARD_PUSH_STALE":
          return t("payout.card.corridorChanged");
        default:
          return t("transfer.prepare.errorGeneric");
      }
    },
    [t]
  );

  const nudgeIncompleteCompose = useCallback(() => {
    setShakePayout(true);
    window.setTimeout(() => setShakePayout(false), 500);
    const scrollToGap = () => {
      const target =
        document.querySelector(".payout-container .payment-required") ??
        document.querySelector(".payout-container .shake") ??
        document.querySelector(".estimated-quote-section");
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    requestAnimationFrame(scrollToGap);
    window.setTimeout(scrollToGap, 50);
  }, []);

  const openAmountLimitSheet = useCallback(
    (kind: "below_min" | "above_max", bound: number, currency: string) => {
      setAmountLimitPopup({ kind, bound, currency });
    },
    [],
  );

  const handleConfirm = useCallback(async () => {
    if (confirmLoading || envelopeLoading) return;

    // Always allow the press: guide the user to whatever is still missing
    // instead of keeping the CTA disabled (send / deferred parity).
    if (!hasAmount || !payoutReady) {
      nudgeIncompleteCompose();
      return;
    }
    // Narrow for compose: payoutReady implies selection, but TS cannot.
    if (!selection) return;
    if (routeUnavailable) return;

    if (amountOutOfRange) {
      if (envelopeDisplayBound) {
        openAmountLimitSheet(
          envelopeVerdict === "below_min" ? "below_min" : "above_max",
          envelopeDisplayBound.amount,
          envelopeDisplayBound.currency,
        );
      }
      return;
    }

    safeCapture(getEvents().CONTINUE_CLICK);

    const linkId = linkIdFromRoute || session.data?.linkId;
    if (!linkId) {
      redirectTo(APP_ROUTES.MINTING_PAGE);
      return;
    }

    setPrepareError(null);
    setConfirmLoading(true);

    let payout = selection.payout;
    const capabilityKey = selection.option?.capabilityKey;

    try {
      // Prefetch miss: one fresh check. Out-of-range → sheet; ok/idle → continue
      // (idle after failure = soft-allow; mint still enforces send authority).
      if (envelopeVerdict === "idle") {
        const retried = await recheckEnvelope();
        if (
          (retried.verdict === "below_min" ||
            retried.verdict === "above_max") &&
          retried.displayBound
        ) {
          openAmountLimitSheet(
            retried.verdict,
            retried.displayBound.amount,
            retried.displayBound.currency,
          );
          return;
        }
      }

      if (
        selection.method === PayoutMethodType.PUSH_TO_CARD &&
        capabilityKey
      ) {
        if (
          !selection.payout.paymentMethodId &&
          (isPayoutCardValidationStale(
            selection.payout.validatedFor,
            sourceCountry,
            destinationCountry
          ) ||
            !selection.payout.cvsValidationId)
        ) {
          setPrepareError(prepareErrorMessage("CARD_PUSH_STALE"));
          return;
        }
        const payoutResult = await preparePayoutRail({
          linkId,
          payout: selection.payout,
          capabilityKey,
          cardholderName: selection.payout.cardholderName,
        });

        payout = sanitizePayoutAfterPrepare(payoutResult.payout);
        setSelection({
          ...selection,
          payout,
        });

        if (payoutResult.status === "failed") {
          safeCapture(getEvents().CARD_REGISTERED, {
            success: false,
            error_code: payoutResult.errorCode,
            purpose: "PAYOUT",
          });
          setPrepareError(prepareErrorMessage(payoutResult.errorCode));
          return;
        }
        safeCapture(getEvents().CARD_REGISTERED, {
          success: true,
          purpose: "PAYOUT",
          payment_method_id: payout.paymentMethodId,
        });
      }

      setDraft(
        buildRequestDraft({
          sourceCountry,
          destinationCountry,
          sourceCurrency,
          destinationCurrency,
          amount: numericAmount,
          payoutMethod: selection.method as FundRequestPayoutMethod,
          payout,
          option: selection.option,
        })
      );

      // Request flow does not run interactive CIP — partner prefill + mint
      // identity minimums (name + identity country) are enough. Sender CIP
      // still applies when a payer fulfills the minted fund-request link.
      const prompt = shouldPromptPhone(session.data, linkId);
      if (prompt) {
        redirectTo(APP_ROUTES.PHONE_PAGE, {
          replace: true,
          state: { next: APP_ROUTES.MINTING_PAGE },
        });
        return;
      }
      await ensureSmsConsent(linkId, i18n.language?.slice(0, 2) || "en");
      redirectTo(APP_ROUTES.MINTING_PAGE, {
        replace: true,
        state: { next: APP_ROUTES.MINTING_PAGE },
      });
    } catch (error) {
      // Link expired / revoked: the dead-link handler already routed to the
      // screen that explains why the link stopped working.
      if (isDeadLinkError(error)) {
        return;
      }

      const code =
        error && typeof error === "object" && "code" in error
          ? String((error as { code?: string }).code)
          : undefined;
      if (code === "REGISTER_FAILED" || code === "VGS_CONFIG_UNAVAILABLE") {
        setPrepareError(prepareErrorMessage(code));
        return;
      }
      navigate(APP_ROUTES.ERROR_PAGE, {
        state: {
          title: t("error.title"),
          subTitle: null,
          message: t("error.tryAgainLater"),
          btnText: t("common.retry"),
          isOut: false,
          pageUrl: APP_ROUTES.TRANSFER_PAGE,
        },
      });
    } finally {
      setConfirmLoading(false);
    }
  }, [
    selection,
    hasAmount,
    payoutReady,
    routeUnavailable,
    confirmLoading,
    envelopeLoading,
    amountOutOfRange,
    envelopeDisplayBound,
    envelopeVerdict,
    recheckEnvelope,
    openAmountLimitSheet,
    nudgeIncompleteCompose,
    setDraft,
    sourceCountry,
    destinationCountry,
    sourceCurrency,
    destinationCurrency,
    numericAmount,
    linkIdFromRoute,
    session.data?.linkId,
    redirectTo,
    navigate,
    t,
    i18n,
    prepareErrorMessage,
    setSelection,
  ]);

  const { openSupport, supportSheet } = useSupportSheet();

  return (
    <>
      <AuraLayout
        colorConfig={colorConfig}
        className="scrollable need-help-header"
      >
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            onBackClick={() => navigate(-1)}
            onIconClick={() => openSupport()}
            icon={<SupportIcon size={35} colors={dropzoneColors} />}
          />
        </Layout.Header>
        <Layout.Content className="transfer-section">
          <ScrollFadeIndicator color={bodyCopy} />
          <section>
            <Title title={t("transfer.title")} align="left" color={TITLE_COLOR} />
            <section
              className={`estimated-quote-section${
                shakePayout && !hasAmount ? " shake" : ""
              }`}
            >
              <section className="quote-container">
                <QuoteInfo
                  label={t("transfer.youRequest")}
                  currency={destinationCurrency}
                  textColor={bodyCopy}
                  mode="input"
                  isActive
                  hideCurrencySymbol={destinationCurrency !== "USD"}
                  icon={<FlagCircle iso2={destinationCountry.toLowerCase()} />}
                  showSelectorChevron
                  onSelect={() => setShowDestinationPopup(true)}
                />
              </section>
            </section>
            <section className="payout-container">
              <PayoutInfo
                title={t("transfer.receiveIn")}
                subtitle={payoutSummary}
                icon={payoutIcon}
                background={backgroundBtn}
                color={textColorBtn}
                locked={!hasAmount}
                onChange={openMethodPicker}
                className={`${payoutPending ? "payment-required" : ""} ${
                  shakePayout && !payoutReady ? "shake" : ""
                }`.trim()}
              />
              {prepareError ? (
                <BodyCopy className="footer-text" textColor={bodyCopy}>
                  <b>{t("transfer.prepare.errorTitle")}</b>
                  <br />
                  {prepareError}
                </BodyCopy>
              ) : routeUnavailable ? (
                <BodyCopy className="footer-text" textColor={bodyCopy}>
                  <b>{t("transfer.routeUnavailableTitle")}</b>
                  <br />
                  {t("transfer.routeUnavailableHint")}
                </BodyCopy>
              ) : (
                <BodyCopy
                  className="footer-text transfer-timing-note"
                  textColor={TITLE_COLOR}
                >
                  <b>{t("transfer.shareableLink")}</b>
                  {" "}
                  <span
                    className="transfer-timing-note-rest"
                    style={{ color: bodyCopy }}
                  >
                    {t("transfer.shareableLinkHint")}
                  </span>
                </BodyCopy>
              )}
            </section>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <div className={`button-wrapper ${showRainbow ? "rainbow-ready" : ""}`}>
              <Button
                size="large"
                background={backgroundBtn}
                color={textColorBtn}
                disabled={confirmLoading || showQuoteLoading}
                onClick={() => void handleConfirm()}
              >
                {confirmLoading
                  ? t("transfer.prepare.loading")
                  : showQuoteLoading
                    ? t("transfer.loadingQuote")
                    : t("transfer.createRequest")}
              </Button>
            </div>
          </section>
        </Layout.Footer>
      </AuraLayout>

      <Popup
        visible={showDestinationPopup}
        onClose={() => setShowDestinationPopup(false)}
      >
        <SelectDestinationCountrySlot
          countries={destinationCountriesForPicker}
          selectedCountry={destinationCountry}
          selectedCurrency={destinationCurrency}
          onSelect={handleDestinationChange}
          onClose={() => setShowDestinationPopup(false)}
        />
      </Popup>

      {amountLimitPopup ? (
        <Popup visible onClose={() => setAmountLimitPopup(null)}>
          <AmountOutOfRangeInfo
            kind={amountLimitPopup.kind}
            bound={amountLimitPopup.bound}
            currency={amountLimitPopup.currency}
            onClose={() => setAmountLimitPopup(null)}
          />
        </Popup>
      ) : null}

      <Popup
        visible={showPayoutPopup}
        onClose={() => setShowPayoutPopup(false)}
      >
        {payoutStep === "method" ? (
          <SelectPayoutMethodSlot
            selectedMethod={selection?.method ?? null}
            selectedPaymentMethodId={
              selection?.method === PayoutMethodType.PUSH_TO_CARD ||
              selection?.method === PayoutMethodType.BANK_ACCOUNT
                ? selection.payout.paymentMethodId ?? null
                : null
            }
            available={availablePayoutMethods}
            cards={destinationCards}
            cardsLoading={destinationCardsLoading}
            accounts={offerableAccounts}
            accountsLoading={accountsLoading}
            loading={payoutPickerLoading}
            bankLabels={bankLabels}
            onPick={handlePayoutPick}
            onClose={() => setShowPayoutPopup(false)}
          />
        ) : (
          <PayoutDetailsSlot
            method={pendingMethod as PayoutMethodType}
            originCountryCode={sourceCountry}
            destinationCountry={destinationCountry}
            destinationCurrency={destinationCurrency}
            options={preQuote?.options ?? []}
            optionsLoading={preQuoteLoading}
            value={
              selection && selection.method === pendingMethod
                ? selection.payout
                : null
            }
            onBack={() => setPayoutStep("method")}
            onClose={() => setShowPayoutPopup(false)}
            onSubmit={handleDetailsSubmit}
          />
        )}
      </Popup>
      {supportSheet}
    </>
  );
});
